// File: src/components/ReservationView.js
import React, { useState, useEffect, useRef } from 'react';
import ReactCalendar from 'react-calendar';
import axios from '../api/axiosInstance';
import showModal from '../components/ui/modals/showModal.js';
import './calendar.css'
import './ReservationView.css';
import ParkingMap from './ParkingMap.js';

import PreferencesModal  from './buttons/preferenceButton/PreferencesModal.js'
import useCalendarPreferences  from '../hooks/preference/useCalendarPreferences.js';

import useNextReservationInfo from '../hooks/useNextReservationInfo';
import useUserReservations from '../hooks/reservation/useUserReservations';
import useDefaultDate from '../hooks/useDefaultDate';

import useCancelButtonState from "../hooks/cancelReservation/useCancelButtonState";
import useBulkCancelReservations from "../hooks/cancelReservation/useBulkCancelReservations";
import useMakeReservation from "../hooks/reservation/useMakeReservation";
import useReserveButtonState from "../hooks/reservation/useReserveButtonState";

import ReserveButton from "./buttons/ReserveButton";
import CancelReservationsButton from "./buttons/CancelReservationsButton";

import {
  fixLocalDate,
  generateRangeDays,
  generateCyclicDays
} from '../utils/dateHelpers';

function ReservationView() {
  const [userId, setUserId] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [calendarValue, setCalendarValue] = useState(null);
  const [calendarKey, setCalendarKey] = useState(0);
  const [regCarNum, setRegCarNum] = useState('');
  const [availableCars, setAvailableCars] = useState([]);
  const [nextReservationInfo, refetchNextReservationInfo] = useNextReservationInfo(userId);
  const [mouseSelecting, setMouseSelecting] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [floors, setFloors] = useState([]);
  const MAX_RESERVATION_MONTHS = 4;

  const {
    cyclicEnabled,
    setCyclicEnabled,
    autoAssignEnabled,
    setAutoAssignEnabled,
    mode,
    setMode,
    applyPreferences,
    resetPreferences
  } = useCalendarPreferences();

  const {
    userReservations,
    userReservedDates,
    isLoadingReservations,
    refetchUserReservations
  } = useUserReservations(userId);


  const lightReset = () => {
    setSelectedSpot(null);
    setCalendarKey((prev) => prev + 1);
  };


  const reserveBtnRef = useRef(null); 
  const calendarRef = useRef(null);
  const lastTouchedDate = useRef(null);

  const HOST = process.env.REACT_APP_HOST;
  const PORT = process.env.REACT_APP_API_PORT;
  const baseUrl = `https://${HOST}`;

  /*const instance = axios.create({
  baseURL,
  ...
});*/

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    axios.get(`${baseUrl}/api/auth/me`)
      .then((res) => {
        setUserId(res.data.id);
      })
      .catch((err) => {
        console.error('Błąd pobierania danych użytkownika', err);
      });
  }, [baseUrl]);
  
  const doFullReset = () => {
    setSelectedDates([]);
    setSelectedSpot(null);
    setCalendarValue(null);
    setCalendarKey((prev) => prev + 1);
  };
  useEffect(() => {
    doFullReset();
    setSelectedSpot(null);
  }, [mode]);

  useDefaultDate(setSelectedDates);

  useEffect(() => {
    if (userId===null) return;
    const token = localStorage.getItem('token');
  
    axios.get(`${baseUrl}/api/user/my-cars`)
    .then(res => {
      const cleaned = res.data.flat().map(car => car.replace(/^'+|'+$/g, ''));
      setAvailableCars(cleaned);
    
      if (!regCarNum && cleaned.length > 0) {
        setRegCarNum(cleaned[0]); // domyślny wybór
      }
    })
    .catch(err => {
      console.error('Błąd pobierania aut użytkownika:', err);
      setAvailableCars([]);
    });
  }, [userId, baseUrl]);

  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && userId !== null) {
      refetchNextReservationInfo();
      initialized.current = true;
    }
  }, [userId]);
  
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setMouseSelecting(false);
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  useEffect(() => {
    const preventDefault = (e) => {
      if (mouseSelecting) {
        e.preventDefault();
      }
    };
  
    document.addEventListener('touchmove', preventDefault, { passive: false });
  
    return () => {
      document.removeEventListener('touchmove', preventDefault);
    };
  }, [mouseSelecting]);

  //Blokujemy scrolla gdy dotykamy palcem po kalendarzu 
  useEffect(() => {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
    if (mouseSelecting && isTouch) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
  
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [mouseSelecting]);

  const handleReset = () => doFullReset();

  const handleCalendarChangeSingle = (val) => {
    if (!val) return doFullReset();
    const d = fixLocalDate(val);
    if (d.getDay() === 0 || d.getDay() === 6) {
      doFullReset();
    } else {
      setSelectedDates([d]);
      setCalendarValue(d);
      setSelectedSpot(null);
    }
  };

  const handleDayClickRange = (date) => {
    const d = fixLocalDate(date);
    if (d.getDay() === 0 || d.getDay() === 6) return;

    setSelectedDates((prev) => {
      if (prev.length >= 2) return [d];
      if (prev.length === 0) return [d];
      if (prev.length === 1) {
        const start = prev[0];
        if (start.getTime() === d.getTime()) return [start];
        const rangeStart = start < d ? start : d;
        const rangeEnd = start < d ? d : start;
        return generateRangeDays(rangeStart, rangeEnd);
      }
      return [d];
    });
    setSelectedSpot(null);
  };

  const handleDayClickCyclic = (date) => {
    const d = fixLocalDate(date);
    if (d.getDay() === 0 || d.getDay() === 6) return;

    setSelectedDates((prev) => {
      if (prev.length >= 2) return [d];
      if (prev.length === 0) return [d];
      if (prev.length === 1) return generateCyclicDays(prev[0], d);
      return [d];
    });
    setSelectedSpot(null);
  };

  const handleDayClick = (date) => {
    if (mode === 'range') handleDayClickRange(date);
    else if (mode === 'cyclic') handleDayClickCyclic(date);
  };

  const handleCalendarChange = (val) => {
    if (mode === 'single') handleCalendarChangeSingle(val);
  };

  const tileDisabled = ({ date, view }) => {
    if (view !== 'month') return false;
    const dow = date.getDay();
    const today = new Date();
    const maxDate = new Date();
    maxDate.setMonth(today.getMonth() + MAX_RESERVATION_MONTHS);
    return dow === 0 || dow === 6 || date > maxDate;
  };

  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
  
    const d = fixLocalDate(date);
    const dateStr = d.toISOString().split('T')[0];
  
    const isSelected = selectedDates.some(sel => sel.toDateString() === d.toDateString());
    const isReserved = userReservedDates.includes(dateStr);
  
    const classes = [];
    
        const today = new Date();
    today.setHours(0,0,0,0);
    if (d < today) {
      classes.push('calendar-day--past');
    }
    if (isReserved) {
      classes.push('react-calendar__tile--user-reserved');
    }
  
    // dla pojedynczego dnia (pojedynczy klik)
    if (mode === 'single' && selectedDates.length === 1 && isSelected) {
      classes.push('react-calendar__tile--selected-day');
    }
  
    // TRYB "SINGLE" JAKO ZAKRES
    if (mode === 'single' && selectedDates.length > 1) {
      const sorted = [...selectedDates].sort((a, b) => a - b);
      const firstTime = sorted[0].getTime();
      const lastTime = sorted[sorted.length - 1].getTime();
      const currentTime = d.getTime();
  
      if (currentTime === firstTime) classes.push('react-calendar__tile--range-start');
      else if (currentTime === lastTime) classes.push('react-calendar__tile--range-end');
      else if (isSelected && currentTime > firstTime && currentTime < lastTime)
        classes.push('react-calendar__tile--range-between');
    }
  
    // tryb cykliczny
    if (mode === 'cyclic' && isSelected) {
      classes.push('react-calendar__tile--selected-day');
    }
  
    return classes.join(' ');
  };

  const renderSelectedDates = () => {
    if (selectedDates.length === 0) return <p>Brak wybranych dat</p>;
    if (selectedDates.length === 1) return <p>{selectedDates[0].toLocaleDateString()}</p>;
    const sorted = [...selectedDates].sort((a, b) => a - b);
    return <p>{sorted[0].toLocaleDateString()} - {sorted[sorted.length - 1].toLocaleDateString()}</p>;
  };

  const handleMouseDownOnDate = (date) => {
    if (mode !== 'single') return;
    if (date.getDay() === 0 || date.getDay() === 6) return;
    setRangeStart(fixLocalDate(date));
    setSelectedDates([fixLocalDate(date)]);
    setMouseSelecting(true);
  };
  
  const handleMouseEnterOnDate = (date) => {
    if (!mouseSelecting || !rangeStart || mode !== 'single') return;
    const end = fixLocalDate(date);
    const start = rangeStart;
    const range = generateRangeDays(start < end ? start : end, start < end ? end : start);
    setSelectedDates(range);
  };
  
  const handleMouseUpOnDate = () => {
    setMouseSelecting(false);
  };
  const handleTouchStart = (date) => {
    if (mode !== 'single') return;
    if (date.getDay() === 0 || date.getDay() === 6) return;
    const d = fixLocalDate(date);
    lastTouchedDate.current = d;
    setRangeStart(d);
    setSelectedDates([d]);
    setMouseSelecting(true);
  };
  
  const handleTouchMove = (event) => {
    if (!mouseSelecting || !rangeStart || mode !== 'single') return;
  
    const touch = event.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
  
    if (!element) return;
  
    const dayAttr = element.getAttribute("data-day"); // 👈 np. "2024-04-10"
    if (!dayAttr) return;
  
    const d = new Date(dayAttr);
    if (isNaN(d.getTime())) return;
  
    const range = generateRangeDays(
      rangeStart < d ? rangeStart : d,
      rangeStart < d ? d : rangeStart
    );
    setSelectedDates(range);
  };
  
  const handleTouchEnd = () => {
    setMouseSelecting(false);
  };

  //konstrukt przycisku do rezerwacji
  const makeReservation = useMakeReservation({
    userId,
    selectedDates,
    selectedSpot,
    regCarNum,
    baseUrl,
    reserveBtnRef,
    userReservedDates,
    autoAssignEnabled,
    refetchUserReservations,
    refetchNextReservationInfo,
    setCalendarKey,
    lightReset
  });

  //konstrukt przycisku do anulowania rezerwacji
  const cancelSelectedReservations = useBulkCancelReservations({
    selectedDates,
    userReservations,
    baseUrl,
    refetchUserReservations,
    refetchNextReservationInfo,
    lightReset,
    setSelectedDates
  });

  //logika przycisku rezerwacji
  const { canReserve, label } = useReserveButtonState({
    selectedDates,
    selectedSpot,
    userReservedDates,
    userId,
    autoAssignEnabled
  });

  //logika przycisku anulowania rezerwacji
  const { shouldDisableCancelButton } = useCancelButtonState({
    selectedDates,
    userReservations
  });

  if (userId === null) {
    return (
      <div className="reservation-container">
        <p>🔄 Ładowanie danych użytkownika...</p>
      </div>
    );
  }
  if (isLoadingReservations) {
    return (
      <div className="reservation-container">
        <p>🔄 Ładowanie rezerwacji...</p>
      </div>
    );
  }
  function formatDateWithWeekday(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  }
  return (
    <div className="reservation-container">
          {showPreferences && (
      <PreferencesModal
      cyclicEnabled={cyclicEnabled}
      setCyclicEnabled={setCyclicEnabled}
      autoAssignEnabled={autoAssignEnabled}
      setAutoAssignEnabled={setAutoAssignEnabled} 
      onApply={({ cyclicEnabled, autoAssignEnabled }) => {
        setCyclicEnabled(cyclicEnabled);
        setAutoAssignEnabled(autoAssignEnabled);
        localStorage.setItem('calendarPreferences', JSON.stringify({
          cyclicEnabled,
          autoAssignEnabled
        }));
        setMode(cyclicEnabled ? 'cyclic' : 'single');
        setShowPreferences(false);
      }}
      onCancel={() => {
        setShowPreferences(false);
      }}
      onReset={() => {
        resetPreferences();
        setShowPreferences(false);
      }}
    />
    )}
        {nextReservationInfo && (
          <div className="daily-info-banner">
            <div className="daily-info-header">
              <span className="status-dot" />
              <span className="reservation-text">
                Masz rezerwację - <strong>{formatDateWithWeekday(nextReservationInfo.date)}</strong>
              </span>
            </div>
            <div className="daily-info-details">
              <div className="info-item">
                <span className="info-label">Piętro</span>
                <div className="info-box">
                  {nextReservationInfo?.ParkingSpot?.floor || 'Nieznane'}
                </div>
              </div>
              <div className="info-item">
                <span className="info-label">Miejsce</span>
                <div className="info-box">
                  {nextReservationInfo?.ParkingSpot?.spotNumber || 'Nieznane'}
                </div>
              </div>
              <div className="info-item">
                <span className="info-label">Nr rejestracyjny</span>
                <div className="info-box">
                  {nextReservationInfo.regCarNum}
                </div>
              </div>
            </div>
          </div>
        )}
      <div className="three-columns">
      <div className={`col-left ${autoAssignEnabled ? "centered-only" : ""}`}>
        <div className="mode-buttons">
          <button className="mode-button" onClick={() => setShowPreferences(true)}>
            ⚙️ Preferencje
          </button>
        <div style={{ marginLeft: 'auto' }}>
        <button
          title="Kliknij, aby zobaczyć instrukcję"
          onClick={() =>
            showModal(`• Wybierz dzień lub przeciągnij myszką / palcem po dniach.\n
Preferencje:
• Zaznaczenie cyklu: Wybierz początek i koniec cyklu "co tydzień" (te same dni tygodnia)
• Automatyczny: Zaznacz dzień lub dni i zarezerwuj - Kalendarz sam wybierze dogodne miejsce.
\nMożna rezerwować maksymalnie na 4 miesiące do przodu.`, {type: "info"})
          }
          style={{
            backgroundColor: '#eee',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            fontSize: '18px',
            fontWeight: 'bold',
            border: '1px solid #aaa',
            cursor: 'pointer'
          }}
        >
          ?
        </button>
      </div>
      </div>
      {availableCars.length > 1 && (
          <div className="car-select">
            <label htmlFor="carSelect">Numer rejestracyjny:</label>
            <select
              id="carSelect"
              value={regCarNum}
              onChange={(e) => setRegCarNum(e.target.value)}
            >
              {availableCars.map((car) => (
                <option key={car} value={car}>
                  {car}
                </option>
              ))}
            </select>
          </div>
        )}
      <div>
        <h4>Wybrane daty:</h4>
          <div className="select-date">
            {renderSelectedDates()}
          </div>
      </div>
      <div ref={calendarRef}>
          <ReactCalendar
            key={calendarKey}
            selectRange={false}
            onChange={mode === 'single' ? handleCalendarChange : undefined}
            onClickDay={handleDayClick}
            value={calendarValue}
            tileDisabled={tileDisabled}
            tileClassName={tileClassName}
            tileContent={({ date, view }) => {
              if (view !== 'month') return null;
              const d = fixLocalDate(date);
              const isWorkday = d.getDay() >= 1 && d.getDay() <= 5;
            
              return (
                <div
                  data-day={d.toISOString().split("T")[0]}
                  onMouseDown={(e) => {
                    if (!isWorkday || mode !== 'single') return;
                    setRangeStart(d);
                    setSelectedDates([d]);
                    setMouseSelecting(true);
                  }}
                  onMouseEnter={(e) => {
                    if (!isWorkday || !mouseSelecting || !rangeStart || mode !== 'single') return;
                    const range = generateRangeDays(
                      rangeStart < d ? rangeStart : d,
                      rangeStart < d ? d : rangeStart
                    );
                    setSelectedDates(range);
                  }}
                  onMouseUp={(e) => {
                    setMouseSelecting(false);
                  }}
                  onTouchStart={(e) => {
                    if (!isWorkday || mode !== 'single') return;
                    setRangeStart(d);
                    setSelectedDates([d]);
                    setMouseSelecting(true);
                  }}
                  onTouchMove={(e) => {
                    if (!isWorkday || !mouseSelecting || !rangeStart || mode !== 'single') return;
            
                    const touch = e.touches[0];
                    let el = document.elementFromPoint(touch.clientX, touch.clientY);
            
                    while (el && !el.getAttribute("data-day")) {
                      el = el.parentElement;
                    }
            
                    if (!el) return;
                    const dayAttr = el.getAttribute("data-day");
                    if (!dayAttr) return;
            
                    const targetDate = fixLocalDate(new Date(dayAttr));
                    const range = generateRangeDays(
                      rangeStart < targetDate ? rangeStart : targetDate,
                      rangeStart < targetDate ? targetDate : rangeStart
                    );
                    setSelectedDates(range);
                  }}
                  onTouchEnd={() => setMouseSelecting(false)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 3,
                    pointerEvents: 'auto',
                  }}
                />
              );
            }}                        
          />
        </div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexDirection: 'column' }}>
            <ReserveButton
              onClick={makeReservation}
              disabled={!canReserve || typeof userId !== "number"}
              reserveBtnRef={reserveBtnRef}
              label={label}
            />
            <CancelReservationsButton
              onClick={cancelSelectedReservations}
              disabled={shouldDisableCancelButton}
            />
          </div>
        </div>
        {!autoAssignEnabled && (
        <div className="col-right">
          <h4>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem' }}>
          <button
            title="Kliknij, aby zobaczyć pomoc"
            onClick={() =>
              showModal("ℹ️ Kliknij na wolne miejsce (białe), aby je wybrać. Miejsca czerwone są zajęte, zielone są Twoje.", {type: "info"})
            }
            style={{
              backgroundColor: '#eee',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              fontSize: '18px',
              fontWeight: 'bold',
              border: '1px solid #aaa',
              cursor: 'pointer'
            }}
          >
            ?
          </button>
          </div>
          Mapa Miejsc Parkingowych</h4>
          <ParkingMap
            selectedDates={selectedDates}
            onSelectSpot={setSelectedSpot}
            selectedSpot={selectedSpot}
            currentUserId={userId}
            mouseSelecting={mouseSelecting}
            floors={floors}
          />
        </div>
        )}
      </div>
      <footer className={`app-footer ${autoAssignEnabled ? "centered-only" : ""}`}>
        <div className="footer-content">
          <img src="/xkom_ambasador.png" alt="Program ambasadorów X-KOM" className="footer-logo" />
          <span> Autor: <strong>Kamil Gajewski</strong></span>
        </div>
      </footer>
    </div>
  );
}

export default ReservationView;
